import { useEffect } from 'react';

function upsertLink(selector, attrs) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement('link');
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([key, value]) => {
    el.setAttribute(key, value);
  });
  return el;
}

function upsertJsonLd(id, data) {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement('script');
    el.type = 'application/ld+json';
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = data;
  return el;
}

function cleanupManagedTags() {
  document.head.querySelectorAll('[data-seo-managed="true"]').forEach((node) => node.remove());
  document.querySelectorAll('script[data-seo-jsonld="true"]').forEach((node) => node.remove());
}

// public/index.html ships the homepage's tags as static markup so crawlers see
// them before any JavaScript runs. Appending a second set of our own left every
// page with duplicate description/og/twitter tags, and since a crawler reads the
// first match it was always reading the static homepage values — the per-route
// SEO was being written but never seen. So update the tag that is already there
// and only create one when it is genuinely missing.
function setMeta(attr, name, value) {
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', value);
  el.setAttribute('data-seo-managed', 'true');
  return el;
}

export default function Seo({
  title,
  description,
  canonical,
  ogType = 'website',
  ogImage = 'https://goodgpt.site/og-image.jpeg?v=c2488723',
  ogImageWidth = '512',
  ogImageHeight = '512',
  noindex = false,
  keywords,
  jsonLd,
  ogImageAlt,
}) {
  useEffect(() => {
    cleanupManagedTags();

    document.title = title;

    setMeta('name', 'description', description);
    setMeta('name', 'robots', noindex
      ? 'noindex,nofollow'
      : 'index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1');
    if (keywords) setMeta('name', 'keywords', keywords);

    setMeta('property', 'og:type', ogType);
    setMeta('property', 'og:site_name', 'GoodGPT');
    setMeta('property', 'og:title', title);
    setMeta('property', 'og:description', description);
    setMeta('property', 'og:url', canonical);
    setMeta('property', 'og:image', ogImage);
    setMeta('property', 'og:image:alt', ogImageAlt || title);
    setMeta('property', 'og:image:width', ogImageWidth);
    setMeta('property', 'og:image:height', ogImageHeight);

    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'twitter:title', title);
    setMeta('name', 'twitter:description', description);
    setMeta('name', 'twitter:image', ogImage);
    setMeta('name', 'twitter:image:alt', ogImageAlt || title);

    upsertLink('link[rel="canonical"]', { rel: 'canonical', href: canonical });

    const blocks = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];
    blocks.forEach((block, i) => {
      const el = upsertJsonLd(`goodgpt-jsonld-${i}`, JSON.stringify(block));
      el.setAttribute('data-seo-jsonld', 'true');
    });

    return () => {
      cleanupManagedTags();
    };
  }, [title, description, canonical, ogType, ogImage, ogImageWidth, ogImageHeight, ogImageAlt, noindex, keywords, jsonLd]);

  return null;
}
